import React, { useState, useCallback } from 'react';
import { down } from "../components/images/svg/icons";
import { handleToggle } from '../helper/salary.helpers';
import { Tooltip } from './../components/images/svg/icons';
import GenericTooltip from './generictooltip';
import { useTranslation } from 'react-i18next';
import useOutsideClick from '../hooks/useOutsideClick';

export type FormFieldProps = {
    type?: 'text' | 'number' | 'select' | 'checkbox' | 'radio',
    label?: string,
    name: string,
    value?: any,
    onChange: (e: React.ChangeEvent<any>) => void,
    options?: Array<{ label: string, value: string | number }>,
    placeholder?: string,
    checked?: boolean,
    [key: string]: any
};

const FormField: React.FC<FormFieldProps> = ({
    type = 'text',
    label,
    name,
    value,
    onChange,
    options = [],
    placeholder = '',
    checked,
    activeTooltipId,
    setActiveTooltipId,
    NoteText,
    isNote = true,
    currency,
    tooltipLabel,
    disabled = false,
    expensesId = false,
    ...rest
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useOutsideClick(() => setIsOpen(false)) as React.MutableRefObject<HTMLDivElement | null>;

    const { t } = useTranslation();

    const handleOptionClick = useCallback((optionValue: string | number) => {
        setIsOpen(false);
        onChange({
            target: { name, value: optionValue },
            currentTarget: { name, value: optionValue }
        } as React.ChangeEvent<any>);
    }, [name, onChange]);

    const getSelectedLabel = useCallback(() => {
        const selectedOption = options.find(opt => opt.value === value);
        return selectedOption?.label || placeholder || '';
    }, [options, value, placeholder]);
    return (
        <div className="form-group mb-2">
            <div>
                {label && <label className="form-label mb-0">{label}</label>}
                {!expensesId &&
                    <span className="vs-tip-anchor">
                        <img
                            src={Tooltip}
                            className="tooltip-svg cursor-pointer"
                            onClick={() => handleToggle(setActiveTooltipId, name)}
                            onMouseEnter={() => setActiveTooltipId(name)}
                            onMouseLeave={() => setActiveTooltipId(null)}
                            alt=""
                        />
                        <GenericTooltip
                            isActive={activeTooltipId === name}
                            content={t(tooltipLabel)}
                            onClose={() => setActiveTooltipId(null)}
                            width="w-60"
                            position="left-0"
                            zIndex="z-index-9"
                        />
                    </span>
                }
            </div>

            {type === 'select' ? (
                <div className="position-relative mt-2" ref={dropdownRef} style={{position:'relative', bottom: '12px'}}>
                    <div
                        className="form-control "
                        onClick={() => !disabled && setIsOpen(!isOpen)} style={{
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            backgroundColor: disabled ? '#f3f4f6' : '#ffffff', // <-- Visual feedback
                            border: isOpen ? '1px solid #a855f7' : '1px solid #EAEBF0',
                            borderRadius: '12px',
                            boxShadow: isOpen ? '0 0 0 3px rgba(168,85,247,0.18)' : 'none',
                            padding: '12px',
                            fontSize: '16px',
                            color: disabled ? '#aaa' : '#333333', // <-- Visual feedback
                            minHeight: '44px',
                            transition: 'border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgb(243, 244, 246)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ffffff';
                        }}
                    >
                        <span style={{
                            color: "#717680"
                        }}>
                            {getSelectedLabel()}
                        </span>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px', // small gap between items
                            marginLeft: '8px'
                        }}>
                            <img
                                src={down}
                                alt=""
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    opacity: disabled ? 0.5 : 1
                                }}
                            />
                            {isNote && (
                                <>
                                    <div style={{
                                        width: '1px',
                                        height: '46px',
                                        background: '#EAEBF0',
                                        margin: '0 8px'
                                    }} />
                                    <span style={{ color: 'rgba(24, 29, 39, 1)', fontSize: '16px' , opacity: 0.5 }}>{currency}</span>
                                </>
                            )}
                        </div>
                    </div>

                    {isNote && <span  className='mt-2 notetext'>{NoteText}</span>}

                    {isOpen && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 50,
                                left: 0,
                                right: 0,
                                backgroundColor: '#ffffff',
                                border: '1px solid #EAEBF0',
                                borderRadius: '6px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                                zIndex: 1000,
                                maxHeight: '200px',
                                overflowY: 'auto'
                            }}
                        >
                            {options.map((opt, index) => (
                                <div
                                    key={opt.value}
                                    onClick={() => handleOptionClick(opt.value)}
                                    style={{
                                        padding: '12px',
                                        color: '#333333',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        transition: 'background-color 0.15s ease',
                                        margin: '4px',
                                        borderRadius: '4px',
                                        backgroundColor: value === opt.value ? 'rgb(243, 244, 246)' : 'transparent'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgb(243, 244, 246)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = value === opt.value ? '#e3f2fd' : 'transparent';
                                    }}
                                >
                                    {opt.label}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : type === 'checkbox' || type === 'radio' ? (
                <div className="form-check ml-0 pl-0">
                    <input
                        className="form-check-input ml-0 pl-0"
                        type={type}
                        name={name}
                        checked={checked}
                        onChange={onChange}
                        {...rest}
                    />
                    {label && <label className="form-check-label ml-0 pl-0">{label}</label>}
                </div>
            ) : (
                <input
                    type={type}
                    className="form-control"
                    name={name}
                    value={value}
                    onChange={onChange}
                    placeholder={placeholder}
                    {...rest}
                />
            )}
        </div>
    );
};

export default FormField;
